import React, { useEffect, useMemo, useState } from 'react';
import { Button, Popover, Tabs, Input, List, Tag, Tooltip, Empty, Space } from 'antd';
import { TagsOutlined, PlusOutlined } from '@ant-design/icons';
import odooOperationSchemas from '../data/odooOperationSchemas';

const schemaCache = new Map();

const operationToModel = (operationType) => {
  switch (operationType) {
    case 'createPO': return 'purchase.order';
    case 'createQuotation':
    case 'createSO':
    case 'confirmSO': return 'sale.order';
    case 'createInvoice':
    case 'postInvoice':
    case 'createCreditNote': return 'account.move';
    case 'createDeliveryNote':
    case 'createGoodsReceive':
    case 'createReturn': return 'stock.picking';
    case 'registerPayment': return 'account.payment';
    case 'lookupPartner': return 'res.partner';
    case 'lookupProduct': return 'product.product';
    case 'lookupStock': return 'stock.quant';
    default: return null;
  }
};

/**
 * Popover-based picker that lists Odoo fields for the current operationType.
 * Click '+' inserts the field path into the bound textarea at the current cursor position.
 *
 * Props：
 *  - operationType
 *  - providerKey (optional) — drives lazy fetch of fields_get cache section
 *  - onInsert(text)
 *  - t (i18n function)
 */
const OdooPromptFieldPicker = ({ operationType, providerKey, onInsert, t }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('master');
  const [search, setSearch] = useState('');
  const [fieldsGet, setFieldsGet] = useState(null);
  const [loading, setLoading] = useState(false);

  const tt = (key, fallback) => (typeof t === 'function' ? (t(key) || fallback) : fallback);

  const schema = useMemo(() => odooOperationSchemas[operationType] || null, [operationType]);
  const odooModel = useMemo(() => operationToModel(operationType), [operationType]);

  // Lazy fetch fields_get when "schema" tab is opened
  useEffect(() => {
    if (!open || activeTab !== 'schema') return;
    if (!providerKey || !odooModel) return;
    const cacheKey = `${providerKey}::${odooModel}`;
    if (schemaCache.has(cacheKey)) {
      setFieldsGet(schemaCache.get(cacheKey));
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`/api/odoo/${encodeURIComponent(providerKey)}/schema/${encodeURIComponent(odooModel)}`,
      { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`schema ${r.status}`)))
      .then(json => {
        if (cancelled) return;
        const fields = (json && json.fields) || null;
        schemaCache.set(cacheKey, fields);
        setFieldsGet(fields);
      })
      .catch(() => {
        if (cancelled) return;
        schemaCache.set(cacheKey, null);
        setFieldsGet(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, activeTab, providerKey, odooModel]);

  const filterByQuery = (rows) => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.path && r.path.toLowerCase().includes(q)) ||
      (r.label && String(r.label).toLowerCase().includes(q))
    );
  };

  const renderRow = (row) => (
    <List.Item
      key={row.path}
      actions={[
        <Tooltip title={tt('workflowDesigner.odoo.promptFieldPickerInsert', '插入')}>
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              if (typeof onInsert === 'function') onInsert(row.path);
            }}
          >
            {tt('workflowDesigner.odoo.promptFieldPickerInsertBtn', 'add')}
          </Button>
        </Tooltip>
      ]}
    >
      <Space direction="vertical" size={2} style={{ width: '100%' }}>
        <Space wrap size={4}>
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.path}</span>
          {row.required && <Tag color="red">{tt('workflowDesigner.odoo.requiredTag', 'required')}</Tag>}
          {row.fk && <Tag color="blue">FK: {row.fk}</Tag>}
          {row.type && <Tag>{row.type}</Tag>}
        </Space>
        {row.label && <span style={{ fontSize: 12, color: '#888' }}>{row.label}</span>}
        {row.tip && <span style={{ fontSize: 12, color: '#666' }}>{row.tip}</span>}
      </Space>
    </List.Item>
  );

  const fkRows = useMemo(() => {
    if (!schema) return [];
    const rows = [];
    [...(schema.master || []), ...(schema.lines || [])].forEach(r => {
      if (r.fk) rows.push(r);
    });
    return rows;
  }, [schema]);

  const fieldsGetRows = useMemo(() => {
    if (!fieldsGet) return [];
    return Object.entries(fieldsGet).map(([fieldName, meta]) => ({
      path: fieldName,
      label: (meta && typeof meta === 'object' && meta.string) ? meta.string : fieldName,
      type: (meta && typeof meta === 'object' && meta.type) ? meta.type : undefined,
      fk: (meta && typeof meta === 'object' && meta.relation) ? meta.relation : undefined
    }));
  }, [fieldsGet]);

  const content = (
    <div style={{ width: 460, maxHeight: 460, display: 'flex', flexDirection: 'column' }}>
      <Input.Search
        allowClear
        placeholder={tt('workflowDesigner.odoo.promptFieldPickerSearch', '搜尋路徑 / 標籤…')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      <Tabs
        size="small"
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'master',
            label: tt('workflowDesigner.odoo.promptFieldPickerMaster', 'Master'),
            children: schema && schema.master?.length > 0
              ? <List
                  size="small"
                  dataSource={filterByQuery(schema.master)}
                  renderItem={renderRow}
                  style={{ maxHeight: 320, overflow: 'auto' }}
                />
              : <Empty description={tt('workflowDesigner.odoo.promptFieldPickerEmpty', '無欄位')} />
          },
          {
            key: 'lines',
            label: tt('workflowDesigner.odoo.promptFieldPickerLines', 'Lines'),
            children: schema && schema.lines?.length > 0
              ? <List
                  size="small"
                  dataSource={filterByQuery(schema.lines)}
                  renderItem={renderRow}
                  style={{ maxHeight: 320, overflow: 'auto' }}
                />
              : <Empty description={tt('workflowDesigner.odoo.promptFieldPickerEmpty', '無欄位')} />
          },
          {
            key: 'fk',
            label: tt('workflowDesigner.odoo.promptFieldPickerFk', 'FK 參照'),
            children: fkRows.length > 0
              ? <List
                  size="small"
                  dataSource={filterByQuery(fkRows)}
                  renderItem={renderRow}
                  style={{ maxHeight: 320, overflow: 'auto' }}
                />
              : <Empty description={tt('workflowDesigner.odoo.promptFieldPickerEmpty', '無 FK')} />
          },
          {
            key: 'schema',
            label: tt('workflowDesigner.odoo.promptFieldPickerSchema', '完整 Schema'),
            children: !providerKey || !odooModel
              ? <Empty description={tt('workflowDesigner.odoo.promptFieldPickerNoProvider',
                  '需設定 Odoo Provider Key 才能載入完整 schema')} />
              : loading
                ? <div style={{ padding: 16 }}>{tt('workflowDesigner.loading', '載入中…')}</div>
                : fieldsGetRows.length > 0
                  ? <List
                      size="small"
                      dataSource={filterByQuery(fieldsGetRows)}
                      renderItem={renderRow}
                      style={{ maxHeight: 320, overflow: 'auto' }}
                    />
                  : <Empty description={tt('workflowDesigner.odoo.promptFieldPickerSchemaEmpty', 'fields_get 為空')} />
          }
        ]}
      />
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      destroyTooltipOnHide
    >
      <Button size="small" icon={<TagsOutlined />}>
        {tt('workflowDesigner.odoo.promptFieldPickerButton', '插入 Odoo 欄位')}
      </Button>
    </Popover>
  );
};

export default OdooPromptFieldPicker;
